import {
  BOTTOM,
  CONTENT_WIDTH,
  DARK,
  GRAY,
  PAGE,
  RADIUS,
  attendanceKinds,
  cardTable,
  condensed,
  courseCell,
  drawCourseCell,
  drawLogo,
  fullName,
  gradeText,
  number,
  periodAttendance,
  periodComment,
  periodGpa,
  periodGrade,
  periodHead,
  sectionGap,
} from './shared'

const TOP = 176
const SIGNATURES = 52
const LINE = [190, 190, 190]
const SOFT = [222, 222, 222]
const HEAD = [242, 242, 242]
const LOOK = { font: 'times', headFill: HEAD, headText: DARK, outline: LINE, divider: SOFT, headFontSize: 7.5, columnLines: true, inset: 0 }

const header = (doc, ctx) => {
  const center = PAGE.width / 2
  if (ctx.logo) {
    const height = 34
    const width = Math.min((ctx.logo.width / ctx.logo.height) * height, 160)
    drawLogo(doc, ctx.logo, center - width / 2, 26, width, height)
  }
  doc.setTextColor(...DARK)
  doc.setFont('times', 'bold')
  doc.setFontSize(15)
  doc.text(ctx.schoolName ?? '', center, 78, { align: 'center' })
  doc.setFont('times', 'normal')
  doc.setFontSize(9.5)
  doc.setTextColor(...GRAY)
  doc.text(ctx.subtitle, center, 92, { align: 'center' })
  doc.setDrawColor(...DARK)
  doc.setLineWidth(1.2)
  doc.line(PAGE.margin, 101, PAGE.width - PAGE.margin, 101)
  doc.setLineWidth(0.4)
  doc.line(PAGE.margin, 104, PAGE.width - PAGE.margin, 104)
}

const infoBox = (doc, card, ctx) => {
  const { t } = ctx
  const top = 114
  const rowHeight = 20
  const cells = [
    [t('Student'), fullName(card.student)],
    ...ctx.fields,
    [t('Academic Year'), ctx.academicYearName],
    [t('Marking Period'), ctx.selected?.name],
  ].filter(([, value]) => value != null && value !== '')
  const perRow = 3
  const rows = Math.ceil(cells.length / perRow)
  const width = CONTENT_WIDTH / perRow
  const height = rows * rowHeight

  doc.setFillColor(...HEAD)
  doc.roundedRect(PAGE.margin, top, CONTENT_WIDTH, height, RADIUS, RADIUS, 'F')
  doc.setDrawColor(...LINE)
  doc.setLineWidth(0.7)
  doc.roundedRect(PAGE.margin, top, CONTENT_WIDTH, height, RADIUS, RADIUS, 'S')
  doc.setDrawColor(...SOFT)
  doc.setLineWidth(0.5)
  cells.forEach(([label, value], index) => {
    const x = PAGE.margin + (index % perRow) * width
    const y = top + Math.floor(index / perRow) * rowHeight
    if (index % perRow) doc.line(x, y + 4, x, y + rowHeight - 4)
    if (index >= perRow && index % perRow === 0) doc.line(PAGE.margin, y, PAGE.margin + CONTENT_WIDTH, y)
    doc.setFont('times', 'bold')
    doc.setFontSize(7)
    doc.setTextColor(...GRAY)
    doc.text(label.toUpperCase(), x + 10, y + 13)
    const labelWidth = doc.getTextWidth(label.toUpperCase())
    doc.setFont('times', 'normal')
    doc.setFontSize(9)
    doc.setTextColor(...DARK)
    doc.text(String(value), x + 16 + labelWidth, y + 13, { maxWidth: width - labelWidth - 26 })
  })
  return top + height
}

const sectionTitle = (doc, title, y, x = PAGE.margin) => {
  doc.setFont('times', 'bold')
  doc.setFontSize(10.5)
  doc.setTextColor(...DARK)
  doc.text(title.toUpperCase(), x, y)
  return y + 7
}

const signatures = (doc, t) => {
  const y = BOTTOM - 14
  const labels = [t('Teacher'), t('Principal'), t('Parent/Guardian')]
  const width = (CONTENT_WIDTH - 40) / 3
  doc.setDrawColor(...DARK)
  doc.setLineWidth(0.5)
  labels.forEach((label, index) => {
    const x = PAGE.margin + index * (width + 20)
    doc.line(x, y, x + width, y)
    doc.setFont('times', 'normal')
    doc.setFontSize(8.5)
    doc.setTextColor(...GRAY)
    doc.text(label, x, y + 11)
  })
}

export default {
  limit: BOTTOM - SIGNATURES,
  render(doc, card, ctx, density) {
    const { t, periods, selected } = ctx
    const d = condensed(density)
    header(doc, ctx)
    let y = infoBox(doc, card, ctx)

    const columns = periods.length + 1
    const body = []
    ;(card.courses ?? []).forEach(course => {
      body.push([
        courseCell(course, d),
        ...periods.map(period => ({ content: periodGrade(course, period), styles: { halign: 'center', fontStyle: 'bold' } })),
        { content: gradeText(course.finalGrade) ?? '—', styles: { halign: 'center', fontStyle: 'bold' } },
      ])
      const comment = d.comments && periodComment(course, selected)
      if (comment) {
        body.push([
          {
            content: `${t('Comment')}: ${comment}`,
            colSpan: columns + 1,
            kind: 'comment',
            styles: { fontStyle: 'italic', fontSize: d.commentFont + 0.5, textColor: GRAY, cellPadding: { top: 0, bottom: d.pad + 1, left: 16, right: 10 } },
          },
        ])
      }
    })

    y = sectionTitle(doc, t('Academic Performance'), y + sectionGap(d))
    y = cardTable(doc, { ...d, font: d.font + 0.5 }, LOOK, {
      startY: y,
      top: TOP,
      head: [[t('Course'), ...periods.map(period => periodHead(period.name)), periodHead(t('Final'))]],
      body: body.length ? body : [[{ content: t('No courses'), colSpan: columns + 1 }]],
      columnStyles: Object.fromEntries(Array.from({ length: columns }, (_, i) => [i + 1, { cellWidth: 44, valign: 'middle' }])),
      skipDivider: data => data.table.body[data.row.index + 1]?.raw?.[0]?.kind === 'comment',
      willDrawCell: data => {
        if (data.section === 'body' && data.cell.raw?.kind === 'course') data.cell.text = []
      },
      didDrawCell: data => {
        if (data.section === 'body' && data.cell.raw?.kind === 'course') drawCourseCell(doc, data.cell, d, 'times')
      },
    })

    const kinds = attendanceKinds(t)
    const gap = 16
    const gpaWidth = 150
    const attendanceX = ctx.hasGpa ? PAGE.margin + gpaWidth + gap : PAGE.margin
    const titleY = y + sectionGap(d) + 6
    let bottom = 0
    if (ctx.hasGpa) {
      sectionTitle(doc, t('GPA'), titleY)
      bottom = cardTable(doc, d, LOOK, {
        startY: titleY + 7,
        top: TOP,
        width: gpaWidth,
        head: [[t('Marking Period'), { content: t('GPA'), styles: { halign: 'center' } }]],
        body: [
          ...periods.map(period => [period.name, { content: number(periodGpa(card.gpa, period)), styles: { halign: 'center' } }]),
          [
            { content: t('Cumulative'), styles: { fontStyle: 'bold' } },
            { content: number(card.gpa?.cumulative), styles: { halign: 'center', fontStyle: 'bold' } },
          ],
        ],
      })
    }
    sectionTitle(doc, t('Attendance'), titleY, attendanceX)
    bottom = Math.max(
      bottom,
      cardTable(doc, d, LOOK, {
        startY: titleY + 7,
        top: TOP,
        x: attendanceX,
        width: PAGE.margin + CONTENT_WIDTH - attendanceX,
        head: [[t('Marking Period'), ...kinds.map(kind => ({ content: kind.label, styles: { halign: 'center' } }))]],
        body: [
          ...periods.map(period => {
            const row = periodAttendance(ctx.attendance, period)
            return [period.name, ...kinds.map(kind => ({ content: row[kind.key] ?? '—', styles: { halign: 'center' } }))]
          }),
          [
            { content: t('Total'), styles: { fontStyle: 'bold' } },
            ...kinds.map(kind => ({ content: ctx.attendance.total?.[kind.key] ?? '—', styles: { halign: 'center', fontStyle: 'bold' } })),
          ],
        ],
      }),
    )

    signatures(doc, t)
    return bottom
  },
}
